import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useStore } from '../../../../hooks/useStore';
import { savePoliticallyExposedPersonMatch } from '../../../../microservices/pep';
import { acceptPeruTermsAndConditions } from '../../../../microservices/users';
import { logout } from '../../../../services/auth';
import { logger } from '../../../../services/logger';
import { translate } from '../../../../services/translate';
import { stores } from '../../../../stores';
import Ui2Form from '../../../ui-2/form/Ui2Form';
import Ui2FormCheckbox from '../../../ui-2/form/checkbox/Ui2FormCheckbox';
import UiAlert from '../../../ui/alert/UiAlert';
import UiBox from '../../../ui/box/UiBox';
import UiButton from '../../../ui/button/UiButton';
import UiCloseButton from '../../../ui/close-button/UiCloseButton';
import UiHeading from '../../../ui/heading/UiHeading';
import Wrapper from './styles';
import Ui2FormRadio from '../../../ui-2/form/radio/Ui2FormRadio';

export default function AuthProfileValidatorPeruTermsAndConditionsAcceptanceModal() {
    const [user, setUser] = useStore(stores.user);
    const [isPeruTermsAndConditionsAcceptanceModalOpen, setIsPeruTermsAndConditionsAcceptanceModalOpen] = useStore(
        stores.modals.isPeruTermsAndConditionsAcceptanceModalOpen,
    );
    const [isAuthenticated] = useStore(stores.isAuthenticated);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const useFormMethods = useForm({ defaultValues: user?.consents });
    const [isPrivacyConfirmationAccepted, areTermsAndConditionsAccepted] = useFormMethods.watch([
        'JUEGOS_PRIVACY_CONFIRMATION',
        'JUEGOS_TERMS_AND_CONDITIONS_CONFIRMATION',
    ]);

    const { formState } = useFormMethods;

    async function tryAcceptTermsAndConditions() {
        if (!user) {
            return;
        }
        setIsLoading(true);
        setError('');

        try {
            useFormMethods.setValue(
                'PEP_CONFIRMATION',
                String(useFormMethods.getValues('PEP_CONFIRMATION')) === 'true',
            );
            await acceptPeruTermsAndConditions(useFormMethods.getValues());
            if (useFormMethods.getValues().hasOwnProperty('PEP_CONFIRMATION')) {
                await savePoliticallyExposedPersonMatch({
                    politicallyExposedPersonText: useFormMethods.getValues().PEP_CONFIRMATION ? '' : 'Myself',
                });
            }
            setIsPeruTermsAndConditionsAcceptanceModalOpen(false);
            setUser({ ...user, consents: {} });
        } catch (error: any) {
            logger.error(
                'AuthProfileValidatorPeruTermsAndConditionsAcceptanceModal',
                'tryAcceptTermsAndConditions',
                error.message,
            );

            setError(translate('Failed to accept terms and conditions', 'ui.common'));
        } finally {
            setIsLoading(false);
        }
    }

    const LABEL_BY_CONSENT_TYPE = {
        JUEGOS_TERMS_AND_CONDITIONS_CONFIRMATION: translate('ui.terms-and-conditions-confirmation'),
        POLAR_KYC_MIGRATION: translate('pe.polar-kyc-migration'),
        JUEGOS_PRIVACY_CONFIRMATION: translate('pe.privacy-confirmation'),
    };

    return (
        <Wrapper open={isPeruTermsAndConditionsAcceptanceModalOpen && isAuthenticated}>
            {user && (
                <UiBox>
                    <UiCloseButton className="close-button" onClick={logout} />

                    <UiHeading title={translate('Self declaration form', 'ui.common')} />

                    <Ui2Form onSubmit={tryAcceptTermsAndConditions} useFormMethods={useFormMethods}>
                        {Object.keys(user.consents).map((consentType) => {
                            return consentType !== 'PEP_CONFIRMATION' ? (
                                <div className="form-control" key={consentType}>
                                    <Ui2FormCheckbox name={consentType} label={LABEL_BY_CONSENT_TYPE[consentType]} />
                                </div>
                            ) : (
                                <div className="form-control" key={consentType}>
                                    <Ui2FormRadio
                                        name={consentType}
                                        label={translate('Politically Exposed Person (PEP) declaration', 'ui.common')}
                                        options={[
                                            {
                                                value: 'false',
                                                label: translate('ui.registration.yes.pep'),
                                            },
                                            {
                                                value: 'true',
                                                label: translate('ui.registration.no.pep'),
                                            },
                                        ]}
                                        error={formState.errors.PEP_CONFIRMATION}
                                        required
                                    />
                                </div>
                            );
                        })}

                        <UiButton
                            type="button"
                            disabled={
                                isLoading ||
                                isPrivacyConfirmationAccepted === false ||
                                areTermsAndConditionsAccepted === false
                            }
                            block
                            color="primary"
                            isFormSubmitButton
                        >
                            {translate('ui.common.consent')}
                        </UiButton>
                    </Ui2Form>

                    {error && <UiAlert failure>{error}</UiAlert>}
                </UiBox>
            )}
        </Wrapper>
    );
}
